import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { Permissions } from '@marketing-milk/interfaces'
import { sessionContext, useGetBusinessIfExists } from '@marketing-milk/frontend'
import { usePacketSurvey, useSprintPacket } from 'hooks/api/useSprintPackets'
import { CommentAnchor, CommentThread } from 'components/comments/CommentThread'
import { AllCommentsDrawer } from 'components/comments/AllCommentsDrawer'
import { anchorSectionKey } from 'components/comments/groupCommentsByAnchor'
import { countUnresolvedTotal } from 'components/comments/commentCounts'
import { readCommentDeepLink } from 'components/comments/commentDeepLink'
import { useCommentAnchors } from './useCommentAnchors'
import { UnresolvedCommentsProvider } from './UnresolvedCommentsContext'
import { PacketSectionsProvider } from './PacketSectionsContext'
import { mergeSections, PacketSection, SECTIONS, visiblePacketSections } from './sections'
import { dark, type } from '../tokens'
import { PresentCover } from './present/PresentCover'
import { PresentProgress } from './present/PresentProgress'
import { PresentPerformance } from './present/sections/PresentPerformance'
import { PresentNarrative } from './present/sections/PresentNarrative'
import { PresentClientSurveySection } from './present/sections/PresentClientSurveySection'
import { PresentAudienceSection } from './present/sections/PresentAudienceSection'
import { PresentBrandStandardsSection } from './present/sections/PresentBrandStandardsSection'
import { PresentCreativeSection } from './present/sections/PresentCreativeSection'
import { PresentCustomSection } from './present/sections/PresentCustomSection'
import { PresentNavLockContext } from './present/PresentNavLock'
import { packetReviewCommentUrl } from '../../creatives/creativeUrl'
import { audiencesUrl } from '../../audiences/audienceUrl'

interface Props {
  businessID: number
  packetID: number
}

/** Compile-time exhaustiveness guard for the section-kind switch. */
const assertNever = (x: never): never => {
  throw new Error(`Unhandled section kind: ${x}`)
}

type Page = { kind: 'cover' } | { kind: 'section'; section: PacketSection; label: string }

export function PacketPresentBody({ businessID, packetID }: Props) {
  const [pageIndex, setPageIndex] = useState(0)
  const { packet, isLoading, isError, refetch } = useSprintPacket(packetID)
  const { data: survey } = usePacketSurvey(packetID, { untilAnswered: true })
  const surveyLink = (survey ?? packet?.survey)?.link ?? null

  // The deck now depends on the packet's AE-authored slides as well as the
  // strategy, so it can't be a module const. `allSections` is every slide the
  // packet has; `sections` is what the client sees — the same predicate Edit
  // numbers against, which is what keeps the two decks' numbers matching.
  const allSections = useMemo(
    () => mergeSections(SECTIONS, packet?.customSections ?? []),
    [packet?.customSections]
  )
  const sections = useMemo(
    () => visiblePacketSections(allSections, packet?.hiddenSections ?? [], surveyLink),
    [allSections, packet?.hiddenSections, surveyLink]
  )

  const PAGES = useMemo<Page[]>(
    () => [
      { kind: 'cover' },
      ...sections.map(section => ({ kind: 'section' as const, section, label: section.label })),
    ],
    [sections]
  )

  const PAGE_LABELS = useMemo(
    () => PAGES.map(p => (p.kind === 'cover' ? 'Cover' : p.label)),
    [PAGES]
  )

  const business = useGetBusinessIfExists()
  const history = useHistory()
  // Client-review-only users (`AccessPacketReview`, no `AccessAdBuilderApp`) can't
  // reach `/edit` at all — `ClientRouter` doesn't route it — so their "exit the
  // deck" destination is the review page instead, both on Esc and on the visible
  // button. Mirrors the same permission check `App.tsx` uses to choose the router.
  const { user } = useContext(sessionContext)
  const hasBuilderAccess = !user || user.permissions.includes(Permissions.AccessAdBuilderApp)
  const { anchor, open, close, setAnchor, setOpen, setRoot } = useCommentAnchors()
  const [navLocked, setNavLocked] = useState(false)
  const [allCommentsOpen, setAllCommentsOpen] = useState(false)
  const [threadTargetCommentId, setThreadTargetCommentId] = useState<number | null>(null)
  const handledDeepLinkRef = useRef(false)
  const handledHashRef = useRef(false)

  // Jump the deck to an anchor's slide and open its thread. `anchorSectionKey`
  // maps every anchor (incl. creative -> 'creative', audience ->
  // 'audience-framework', performance-metric -> 'performance-review') to a real
  // section slide, so entity anchors land explicitly. Shared by the Slack
  // deep-link effect and the "All comments" list. `commentId` drives
  // CommentThread's in-app highlight; omit it for deep-links (it reads ?commentId=).
  const openAnchor = useCallback(
    (target: CommentAnchor, commentId?: number | null) => {
      const key = anchorSectionKey(target.type, target.id)
      const idx = PAGES.findIndex(p => p.kind === 'section' && p.section.key === key)
      if (idx >= 0) setPageIndex(idx)
      setThreadTargetCommentId(commentId ?? null)
      setAnchor(target)
      setOpen(true)
    },
    [setAnchor, setOpen, PAGES]
  )

  // Slack comment deep-link (`?commentId=&anchorType=&anchorId=`): jump the deck
  // to the matching slide, then open the thread. The ref latch keeps this run-once
  // so a packet refetch can't yank the viewer back mid-deck.
  //
  // Every anchor now resolves straight to its section's slide, so `packet` — which
  // is what `sections`, and therefore `PAGES`, is built from — is the only thing
  // worth waiting on.
  useEffect(() => {
    if (!packet) return
    if (handledDeepLinkRef.current) return
    const link = readCommentDeepLink()
    if (!link) return
    handledDeepLinkRef.current = true
    openAnchor({ type: link.anchorType, id: link.anchorId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packet])

  // The review surface links back as `/present#creative`, so the client returns to
  // the section they left rather than the cover. Latched like the deep-link above:
  // a packet refetch rebuilds `PAGES`, and re-running would yank a viewer who has
  // since paged on.
  useEffect(() => {
    if (!packet || handledHashRef.current) return
    const key = window.location.hash.replace('#', '')
    if (!key) return
    handledHashRef.current = true
    const idx = PAGES.findIndex(p => p.kind === 'section' && p.section.key === key)
    if (idx >= 0) setPageIndex(idx)
  }, [packet, PAGES])

  const closeThread = useCallback(() => {
    close()
    setThreadTargetCommentId(null)
  }, [close])

  const onSelectComment = useCallback(
    (target: CommentAnchor, commentId: number) => {
      setAllCommentsOpen(false)
      // Ads no longer appear in the deck, so a creative comment has no card to jump
      // to. Send an employee to the review surface, where the ad's history column
      // shows the comment beside the ad it is about — the same destination its
      // Slack notification links to. A client stays on this comment surface, which
      // is the one they can read.
      if (target.type === 'creative' && hasBuilderAccess) {
        window.open(
          packetReviewCommentUrl(businessID, packetID, target.id, commentId),
          '_blank',
          'noopener'
        )
        return
      }
      openAnchor(target, commentId)
    },
    [openAnchor, hasBuilderAccess, businessID, packetID]
  )

  const unresolvedTotal = useMemo(
    () => countUnresolvedTotal(packet?.comments ?? []),
    [packet?.comments]
  )

  const exitDeck = useMemo(
    () => (index: number) => {
      if (!hasBuilderAccess) {
        history.push(`/${businessID}/sprint-packets/${packetID}/review`)
        return
      }
      const page = PAGES[index]
      const hash = page.kind === 'section' ? `#${page.section.key}` : ''
      history.push(`/${businessID}/sprint-packets/${packetID}/edit${hash}`)
    },
    [hasBuilderAccess, history, businessID, packetID, PAGES]
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack keys while the AE is typing in the live comment drawer, an
      // open in-slide modal, or the "All comments" list —
      // arrow keys must move the text cursor/list and Esc must close the open
      // surface, not discard work or move the deck.
      if (open || navLocked || allCommentsOpen) return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      if (e.key === 'ArrowRight') setPageIndex(i => Math.min(i + 1, PAGES.length - 1))
      else if (e.key === 'ArrowLeft') setPageIndex(i => Math.max(i - 1, 0))
      else if (e.key === 'Escape') exitDeck(pageIndex)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pageIndex, exitDeck, open, navLocked, allCommentsOpen, PAGES.length])

  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: dark.bg,
    color: dark.text,
    fontFamily: type.family.sans,
  }

  if (isLoading) {
    return (
      <div
        style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress size={28} sx={{ color: dark.accent }} />
      </div>
    )
  }

  if (isError || !packet) {
    return (
      <div
        style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: type.family.serif,
              fontSize: type.size.h1,
              fontWeight: type.weight.regular,
            }}
          >
            Couldn't load this sprint packet
          </h2>
          <button
            type="button"
            onClick={() => refetch()}
            style={{
              alignSelf: 'center',
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${dark.hairline}`,
              background: dark.surface,
              color: dark.text,
              fontFamily: type.family.mono,
              fontSize: type.size.eyebrow,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const renderSection = (s: PacketSection) => {
    switch (s.kind) {
      case 'custom':
        return <PresentCustomSection palette={dark} packetID={packet.id} section={s.section} />
      case 'performance':
        return <PresentPerformance palette={dark} packet={packet} />
      case 'narrative':
        return (
          <PresentNarrative
            palette={dark}
            sectionKey={s.key}
            label={s.label}
            packetID={packet.id}
            narrative={packet.narrative ?? []}
          />
        )
      case 'client-survey':
        // `sections` only contains this slide when the link is set.
        return (
          <PresentClientSurveySection
            palette={dark}
            packetID={packet.id}
            surveyLink={surveyLink ?? ''}
            answered={survey?.answered ?? false}
          />
        )
      case 'audience':
        return (
          <PresentAudienceSection
            palette={dark}
            packetID={packet.id}
            audiences={packet.audiences}
            audienceSelection={packet.audienceSelection ?? null}
          />
        )
      case 'brand':
        return (
          <PresentBrandStandardsSection
            palette={dark}
            packetID={packet.id}
            brandStandards={packet.brandStandards ?? null}
            brandLogos={packet.brandLogos}
          />
        )
      case 'creative':
        return (
          <PresentCreativeSection palette={dark} businessID={businessID} packetID={packet.id} />
        )
      default:
        // Exhaustive over SectionKind today; a new kind fails the type-check here.
        return assertNever(s)
    }
  }

  return (
    <PacketSectionsProvider sections={sections}>
      <UnresolvedCommentsProvider comments={packet.comments}>
        <PresentNavLockContext.Provider value={setNavLocked}>
          <style>{`
        .present-slide-scroll::-webkit-scrollbar { width: 8px; }
        .present-slide-scroll::-webkit-scrollbar-track { background: transparent; }
        .present-slide-scroll::-webkit-scrollbar-thumb { background: ${dark.hairline}; border-radius: 4px; }
        .present-slide-scroll::-webkit-scrollbar-thumb:hover { background: ${dark.textFaint}; }
      `}</style>
          <div ref={setRoot} style={baseStyle}>
            <button
              type="button"
              onClick={() => setAllCommentsOpen(true)}
              style={{
                position: 'absolute',
                top: 20,
                left: 24,
                zIndex: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 6,
                border: `1px solid ${dark.hairline}`,
                background: dark.surface,
                color: dark.textMuted,
                fontFamily: type.family.mono,
                fontSize: type.size.eyebrow,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              All comments
              {unresolvedTotal > 0 && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 16,
                    height: 16,
                    padding: '0 5px',
                    borderRadius: 999,
                    background: dark.accent,
                    color: dark.accentInk,
                    fontSize: 10,
                    fontWeight: type.weight.semibold,
                    lineHeight: 1,
                  }}
                >
                  {unresolvedTotal}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => exitDeck(pageIndex)}
              style={{
                position: 'absolute',
                top: 20,
                right: 24,
                zIndex: 2,
                padding: '6px 12px',
                borderRadius: 6,
                border: `1px solid ${dark.hairline}`,
                background: dark.surface,
                color: dark.textMuted,
                fontFamily: type.family.mono,
                fontSize: type.size.eyebrow,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {hasBuilderAccess ? 'Esc · Editor' : 'Esc · Review'}
            </button>

            {PAGES.map((page, i) => {
              const isActive = i === pageIndex
              return (
                <div
                  key={i}
                  aria-hidden={!isActive}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: isActive ? 1 : 0,
                    pointerEvents: isActive ? 'auto' : 'none',
                    // `visibility: hidden` pulls inactive slides out of the tab order and
                    // the accessibility tree (their comment triggers would otherwise stay
                    // focusable). Delay the flip on the way out so the 200ms fade still plays.
                    visibility: isActive ? 'visible' : 'hidden',
                    transition: isActive
                      ? 'opacity 200ms ease'
                      : 'opacity 200ms ease, visibility 0s linear 200ms',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    className="present-slide-scroll"
                    style={{
                      width: '100%',
                      maxWidth: 1024,
                      height: '100%',
                      overflowY: 'auto',
                      // Top padding keeps a visible step below the fixed
                      // All Comments / Esc chrome (top: 20) while biasing
                      // content upward — vertical space is scarce when presenting.
                      padding: '40px 48px 88px',
                      scrollbarWidth: 'thin',
                      scrollbarColor: `${dark.hairline} transparent`,
                    }}
                  >
                    {page.kind === 'cover' ? (
                      <PresentCover
                        palette={dark}
                        packet={packet}
                        businessName={business?.businessName}
                        phase={business?.phase}
                      />
                    ) : (
                      renderSection(page.section)
                    )}
                  </div>
                </div>
              )
            })}

            <PresentProgress
              palette={dark}
              count={PAGES.length}
              current={pageIndex}
              onJump={setPageIndex}
              labels={PAGE_LABELS}
            />
          </div>

          <AllCommentsDrawer
            open={allCommentsOpen}
            onClose={() => setAllCommentsOpen(false)}
            palette={dark}
            comments={packet.comments}
            // The unfiltered deck, exactly as in Edit — mid-meeting, an AS
            // opening comments on a hidden slide must still see it by name in
            // the list and the location filter.
            sections={allSections}
            audiences={packet.audiences}
            onSelectComment={onSelectComment}
          />

          <CommentThread
            open={open}
            onClose={closeThread}
            businessID={businessID}
            anchor={anchor}
            targetCommentId={threadTargetCommentId}
            // Refetch the packet on a comment mutation so hydrated comments (the badge
            // source) refresh and slide-header unresolved badges clear on resolve.
            onCommentsChanged={refetch}
            notificationContext={{
              header: `Sprint Packet — ${packet.sprint?.name ?? `#${packet.sprintID}`}`,
              pageUrlBase: window.location.href.split('?')[0],
              pageUrlBaseForAnchor: a =>
                a.type === 'audience' ? audiencesUrl(businessID) : undefined,
              pageUrlForAnchor: (a, commentId) =>
                a.type === 'creative'
                  ? packetReviewCommentUrl(businessID, packetID, a.id, commentId)
                  : undefined,
              slackChannelId: business?.slackChannelId,
            }}
          />
        </PresentNavLockContext.Provider>
      </UnresolvedCommentsProvider>
    </PacketSectionsProvider>
  )
}
